import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { InterfaceEmployee } from 'app/models/employee.model';
import { EmployeeService } from 'app/services/employee.service';
import { timezone } from 'assets/constants/timezone';
import { AngularFireStorage } from '@angular/fire/storage';
import { Observable, OperatorFunction, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, take, tap } from 'rxjs/operators';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezoneDayJs from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezoneDayJs);

@Component({
  selector: 'app-create-job',
  templateUrl: './create-job.component.html',
  styleUrls: ['./create-job.component.scss']
})
export class CreateJobComponent implements OnInit {

  jobForm: FormGroup;
  isSaving = false;
  supervisor: InterfaceEmployee;
  auditor: InterfaceEmployee;
  supervisors: InterfaceEmployee[] = [];
  auditors: InterfaceEmployee[] = [];
  userId: string;
  userName: string;
  currentPercentage = 0;
  subscriptions: Subscription[] = [];
  isUploadingFile = false;
  downloadURL: Observable<string> = new Observable;

  searching = false;
  searchFailed = false;
  searchSupervisor: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.searching = true),
      switchMap(term =>
        this.employeeService.searchEmployee(term, true, 'supervisor').pipe(
          tap(() => this.searchFailed = false),
          catchError(() => {
            this.searchFailed = true;
            return of([]);
          }))
      ),
      tap(() => this.searching = false)
    );
  searchAuditor: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.searching = true),
      switchMap(term =>
        this.employeeService.searchEmployee(term, true, 'auditor').pipe(
          tap(() => this.searchFailed = false),
          catchError(() => {
            this.searchFailed = true;
            return of([]);
          }))
      ),
      tap(() => this.searching = false)
    );
  formatter = (x: { fullName: string }) => x.fullName;

  constructor(
    public ngbActiveModal: NgbActiveModal,
    protected fb: FormBuilder,
    protected employeeService: EmployeeService,
    protected storage: AngularFireStorage
  ) {
    this.jobForm = this.fb.group({
      name: [null, Validators.required],
      url: [null, Validators.required]
    });
    this.userName = localStorage.getItem("userName") || "Not found user";
    this.userId = localStorage.getItem("userId") || "Not found user";
  }

  ngOnInit(): void {
  }

  selectSupervisor(supervisor: InterfaceEmployee): void {
    this.supervisors = [];
    this.supervisor = supervisor;
  }

  selectAuditor(auditor: InterfaceEmployee): void {
    this.auditors = [];
    this.auditor = auditor;
  }

  uploadFile(target: any): void {
    let fileUploadSub: Subscription;
    this.isUploadingFile = true;
    this.currentPercentage = 0;
    // getting the file uploaded
    const file = target.files[0];
    // creating the path for the storage
    const filePath = `Jobs/Description/${Date.now()}`;
    // reference to storage
    const fileRef = this.storage.ref(filePath);
    // object to upload the file
    const task = this.storage.upload(filePath, file);
    fileUploadSub = task.percentageChanges().subscribe((percentage: any) => {
      this.currentPercentage = percentage;
    }, (error: any) => {
      console.error(error);
    });
    // checking status of the upload
    this.subscriptions.push(
      task.snapshotChanges().pipe(
        finalize(() => {
          this.downloadURL = fileRef.getDownloadURL();
          this.subscriptions.push(
            // getting the url to download
            this.downloadURL.subscribe(url => {
              if (url) {
                fileUploadSub.unsubscribe();
                this.jobForm.controls.url.patchValue(url);
                this.jobForm.controls.url.updateValueAndValidity();
                this.isUploadingFile = false;
              }
            }, (error: any) => {
              console.error(error);
            })
          );
        })
      ).subscribe((subscribeResult: any) => {
        console.log(subscribeResult);
      }, (error: any) => {
        console.error(error);
      })
    );
  }

  downloadFile(): void {
    window.open(this.jobForm.value.url);
  }

  save(): void {
    this.isSaving = true;
    const newJob = {
      ...this.jobForm.value,
      auditorId: this.auditor.id,
      auditorName: this.auditor.name + " " + this.auditor.lastName,
      supervisorId: this.supervisor.id,
      supervisorName: this.supervisor.name + " " + this.supervisor.lastName,
      id: this.employeeService.createId(),
      createdById: this.userId,
      createdByName: this.userName,
      createdAt: dayjs().tz(timezone).unix(),
    };
    this.employeeService.createJob(newJob).then((result: any) => {
      this.ngbActiveModal.close({
        success: true,
        result
      });
    }).catch((error: any) => {
      console.error(error);
      this.ngbActiveModal.close({
        success: false,
        error
      });
    });
  }

}
